import { useMemo } from 'react';
import type { AdminPage } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { isExtensionVisible } from '@atlassian/jira-forge-ui-utils/src/utils/extension/index.tsx';
import { forgeAdminModuleResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { useForgeItemStack } from '../../common/utils/find-item-stack/index.tsx';
import type { UseAdminPageNavigationResourceReturnWithStack } from '../types.tsx';

const configPageFilter = (extension: AdminPage) => extension.properties.useAsConfig;
const getStartedPageFilter = (extension: AdminPage) => extension.properties.useAsGetStarted;
const adminPageFilter = (extension: AdminPage) =>
	!configPageFilter(extension) && !getStartedPageFilter(extension);

export const useAdminForgeApps = (
	routeName?: string,
): UseAdminPageNavigationResourceReturnWithStack<AdminPage[]> => {
	const { data, loading, error } = useResource(forgeAdminModuleResource);
	const visibleExtensions = data ? data.filter(isExtensionVisible) : null;
	const stackData = visibleExtensions ? visibleExtensions.filter(adminPageFilter) : null;
	const stack = useForgeItemStack(stackData, routeName);

	return useMemo<UseAdminPageNavigationResourceReturnWithStack<AdminPage[]>>(() => {
		if (!visibleExtensions) {
			return {
				data: null,
				loading,
				error,
				stack,
			};
		}

		return {
			data: {
				adminPage: visibleExtensions.filter(adminPageFilter),
				configPage: visibleExtensions.filter(configPageFilter),
				getStartedPage: visibleExtensions.filter(getStartedPageFilter),
			},
			loading,
			error,
			stack,
		};
	}, [visibleExtensions, loading, error, stack]);
};
